import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const packageModalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  content: {
    position: "relative",
    inset: "unset",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    transform: "none",
    width: "min(640px, 100%)",
    maxHeight: "min(90vh, 720px)",
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
};

const emptyModalData = {
  Name: "",
  Duration: 12,
  Amount: 0.0,
  Status: true,
  Description: "",
  Features: "",
  IncludedPonds: 1,
  ExtraPondAmount: 0,
  AllowsExtraPonds: false,
};

function featuresToText(features) {
  if (!features) return "";
  if (Array.isArray(features)) return features.join("\n");
  try {
    const parsed = JSON.parse(features);
    return Array.isArray(parsed) ? parsed.join("\n") : "";
  } catch {
    return String(features);
  }
}

const ManagePackages = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalData, setModalData] = useState(emptyModalData);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchPackagesPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get("/api/packages/GetAll", {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      throw error;
    }
  }, []);

  const {
    rows: packages,
    totalCount,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchPackagesPage);

  const openModal = (data = null) => {
    if (data) {
      setIsEditing(true);
      setEditId(data.id);
      setModalData({
        ...emptyModalData,
        ...data,
        Features: featuresToText(data.Features),
        IncludedPonds: data.IncludedPonds ?? 1,
        ExtraPondAmount: data.ExtraPondAmount ?? 0,
        AllowsExtraPonds: Boolean(data.AllowsExtraPonds),
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setModalData(emptyModalData);
    }
    setModalIsOpen(true);
  };

  const closeModal = () => setModalIsOpen(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = {
      ...modalData,
      [name]: type === "checkbox" ? checked : value,
    };
    if (name === "ExtraPondAmount") {
      const extra = parseFloat(value);
      if (Number.isFinite(extra) && extra > 0) {
        next.AllowsExtraPonds = true;
      }
    }
    setModalData(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!modalData.Name.trim()) {
      showErrorToast("Name is required");
      return;
    }
    const duration = parseInt(modalData.Duration, 10);
    if (isNaN(duration) || duration <= 0) {
      showErrorToast("Duration must be a positive integer");
      return;
    }
    const amount = parseFloat(modalData.Amount);
    if (isNaN(amount) || amount < 0) {
      showErrorToast("Amount must be a non-negative number");
      return;
    }
    const includedPonds = parseInt(modalData.IncludedPonds, 10);
    if (isNaN(includedPonds) || includedPonds < 1) {
      showErrorToast("Included ponds must be at least 1");
      return;
    }
    const extraPondAmount = parseFloat(modalData.ExtraPondAmount);
    if (isNaN(extraPondAmount) || extraPondAmount < 0) {
      showErrorToast("Extra pond price must be non-negative");
      return;
    }

    const payload = {
      ...modalData,
      Duration: duration,
      Amount: amount,
      IncludedPonds: includedPonds,
      ExtraPondAmount: extraPondAmount,
      AllowsExtraPonds: modalData.AllowsExtraPonds || extraPondAmount > 0,
      Features: modalData.Features,
    };

    try {
      setIsSubmitting(true);
      if (isEditing) {
        await axiosInstance.put(`/api/packages/Update/${editId}`, payload);
        showSuccessToast("Package updated successfully");
      } else {
        await axiosInstance.post("/api/packages/Add", payload);
        showSuccessToast("Package added successfully");
      }
      await refetch();
      closeModal();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deletePackage = async (pkgId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setIsSubmitting(true);
        await axiosInstance.delete(`/api/packages/Delete/${pkgId}`);
        await refetch();
        showSuccessToast("Package deleted successfully");
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast("Error deleting package.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading && packages.length === 0) {
    return <Preloader />;
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Manage Packages</li>
          </ul>
          <h1 className="page-header mb-0">Manage Packages</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-success btn-rounded px-4 rounded-pill"
            onClick={() => openModal()}
            aria-label="Add package"
            data-testid="add-package"
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true"></i>
            Add
          </button>
        </div>
      </div>
      <div className="card border-0">
        <div className="tab-content p-3">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Duration</th>
                <th>Amount</th>
                <th>Included ponds</th>
                <th>Extra pond $</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5">
                    Loading…
                  </td>
                </tr>
              ) : (
                packages.map((pkg) => (
                  <tr key={pkg.id}>
                    <td>{pkg.Name}</td>
                    <td>{pkg.Duration}</td>
                    <td>${Number(pkg.Amount).toFixed(2)}</td>
                    <td>{pkg.IncludedPonds ?? 1}</td>
                    <td>${Number(pkg.ExtraPondAmount ?? 0).toFixed(2)}</td>
                    <td>{pkg.Status ? (
                      <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                        <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                        Active
                      </span>
                    ) : (
                      <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                        <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                        Pending
                      </span>
                    )}</td>
                    <td>
                      <span onClick={() => openModal(pkg)}>
                        <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                      </span>
                      &nbsp;&nbsp;
                      <span>
                        <i
                          onClick={() => deletePackage(pkg.id)}
                          className="fa fa-trash fs-13px fa-fw me-5px"
                        ></i>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <ListPagination
            currentPage={currentPage}
            totalCount={totalCount}
            entriesPerPage={entriesPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={packageModalStyles}
        className="donezo-form-modal"
        overlayClassName="donezo-form-modal-overlay"
        contentLabel={isEditing ? "Edit package" : "Add package"}
      >
        <form
          onSubmit={handleSubmit}
          className="d-flex flex-column h-100"
          style={{ maxHeight: "min(90vh, 720px)" }}
        >
          <div className="donezo-form-modal__header">
            <span className="donezo-form-modal__icon" aria-hidden>
              <i className="fa-solid fa-boxes-packing" />
            </span>
            <div className="flex-grow-1 min-w-0">
              <h2 className="donezo-form-modal__title">
                {isEditing ? "Edit Package" : "Add Package"}
              </h2>
              <p className="donezo-form-modal__subtitle">
                Configure pricing, pond limits, and customer-facing marketing copy.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-link donezo-form-modal__close p-2"
              onClick={closeModal}
              aria-label="Close"
            >
              <i className="fas fa-times fa-lg" />
            </button>
          </div>

          <div className="donezo-form-modal__body">
            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">
                Package details
              </h3>
              <label htmlFor="Name" className="form-label small fw-medium mb-1">
                Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control mb-3"
                placeholder="e.g. Standard Package"
                id="Name"
                name="Name"
                value={modalData.Name}
                onChange={handleChange}
                autoFocus
              />
              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label htmlFor="Duration" className="form-label small fw-medium mb-1">
                    Duration (months) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="form-control"
                    id="Duration"
                    name="Duration"
                    value={modalData.Duration}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-sm-6">
                  <label htmlFor="Amount" className="form-label small fw-medium mb-1">
                    Base amount ($) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    id="Amount"
                    name="Amount"
                    value={modalData.Amount}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <label htmlFor="Description" className="form-label small fw-medium mb-1">
                Description
              </label>
              <textarea
                className="form-control mb-3"
                id="Description"
                name="Description"
                rows={3}
                value={modalData.Description || ""}
                onChange={handleChange}
                placeholder="Short intro shown on the package card (HTML paragraphs such as &lt;p&gt;...&lt;/p&gt; are supported)"
              />
              <label htmlFor="Features" className="form-label small fw-medium mb-1">
                Features (one per line)
              </label>
              <textarea
                className="form-control"
                id="Features"
                name="Features"
                rows={5}
                value={modalData.Features || ""}
                onChange={handleChange}
                placeholder="Relative Weight&#10;Catch Per Unit Effort (CPUE)"
              />
            </section>

            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">
                Pond limits
              </h3>
              <div className="row g-3">
                <div className="col-sm-6">
                  <label htmlFor="IncludedPonds" className="form-label small fw-medium mb-1">
                    Included ponds
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    id="IncludedPonds"
                    name="IncludedPonds"
                    value={modalData.IncludedPonds}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-sm-6">
                  <label htmlFor="ExtraPondAmount" className="form-label small fw-medium mb-1">
                    Extra pond price ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    id="ExtraPondAmount"
                    name="ExtraPondAmount"
                    value={modalData.ExtraPondAmount}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-check form-switch mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="AllowsExtraPonds"
                  name="AllowsExtraPonds"
                  checked={Boolean(modalData.AllowsExtraPonds)}
                  onChange={handleChange}
                />
                <label className="form-check-label fw-medium" htmlFor="AllowsExtraPonds">
                  Allows extra ponds (Multi-Pond)
                </label>
              </div>
            </section>

            <section className="donezo-form-modal__section">
              <h3 className="donezo-form-modal__section-title">
                Availability
              </h3>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="Status"
                  name="Status"
                  checked={modalData.Status}
                  onChange={handleChange}
                />
                <label className="form-check-label fw-medium" htmlFor="Status">
                  Active
                </label>
              </div>
            </section>
          </div>

          <div className="donezo-form-modal__footer">
            <button type="button" className="btn btn-outline-secondary px-4" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary px-4" disabled={isSubmitting}>
              <i className={`fas ${isSubmitting ? "fa-spinner fa-spin" : isEditing ? "fa-check" : "fa-plus"} me-2`} aria-hidden />
              {isSubmitting ? "Saving…" : isEditing ? "Save changes" : "Add package"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManagePackages;
